import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { SEOHead, organizationSchema, mobileAppSchema, BRAND } from './SEOUtils';

export default function BlackDatingAppPage() {
  const schemas = [organizationSchema, mobileAppSchema];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Black Dating App for Conscious Relationships | UnityLove"
        description="UnityLove is the Black dating app for intentional connection—poly-inclusive (polyamory + polygamy) and welcoming to monogamy. Find meaningful Black love."
        canonical="/black-dating-app"
        schemas={schemas}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            The Black Dating App for Those Who Want More Than Swipes
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            UnityLove is an Afrocentric dating platform where Black singles seeking meaningful, committed relationships connect through shared values, intention, and authentic conversation.
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            While UnityLove is proudly Black poly-centered, it is also a welcoming home for intentional monogamy. If you value honesty, commitment, and clarity in how you love, you belong here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Join Free Today
            </Link>
            <a
              href={BRAND.playStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Download App
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Why Choose a Black-Centered Dating App?
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Mainstream dating apps often leave Black users feeling like afterthoughts—dealing with biased algorithms, fetishization, and a sea of incompatible matches. UnityLove was built by and for Black people who deserve a dating experience that centers their needs.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            On UnityLove, you'll find a community that understands Black culture, values, and the unique experience of dating while Black. No explaining your hair, your family dynamics, or your lived experience—just connecting with people who get it.
          </p>
          <p className="text-lg text-slate-700">
            Whether you're looking for casual dating, serious relationships, or exploring <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black poly dating</Link>, UnityLove provides the space for authentic connection.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            What Makes UnityLove Different from Other Black Dating Apps
          </h2>
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="bg-rose-100 text-rose-600 rounded-full p-3 flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Intention-First Matching</h3>
                <p className="text-slate-700">We match based on what you're actually looking for—not just proximity and photos. Define your relationship goals and find people aligned with them.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="bg-rose-100 text-rose-600 rounded-full p-3 flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">All Relationship Structures Welcome</h3>
                <p className="text-slate-700">Monogamous, polyamorous, polygamous, open relationships—we support how you choose to love. No judgment, just options.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="bg-rose-100 text-rose-600 rounded-full p-3 flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Meaningful Conversations</h3>
                <p className="text-slate-700">Voice messages, video chat, and conversation prompts that go beyond "hey." Build connection before you meet.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="bg-rose-100 text-rose-600 rounded-full p-3 flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Safety & Verification</h3>
                <p className="text-slate-700">Photo verification, ID checks for Elite members, and moderation that prioritizes user safety.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Features Black Singles Actually Want
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            We asked the community what was missing from other dating apps. Here's what we built:
          </p>
          <ul className="space-y-3 text-lg text-slate-700">
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span>Speed dating events for quick, meaningful connections</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span>Lounge feature for casual video encounters</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span>Relationship structure filters (monogamous, poly, polygamy, ENM, couples)</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span>Value-based matching beyond surface preferences</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span>Community rooms for group discussions</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-rose-600">✓</span>
              <span>Gift-giving to show appreciation</span>
            </li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Black Dating, Your Way
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Whether you're a Black professional looking for a serious partner, someone exploring <Link to="/ethical-non-monogamy-dating" className="text-rose-600 hover:text-rose-700 underline">ethical non-monogamy</Link>, or anywhere in between, UnityLove adapts to your needs.
          </p>
          <p className="text-lg text-slate-700">
            We're not trying to be everything to everyone. We're building the best Black dating app—period. An Afrocentric dating platform that celebrates Black love in all its forms.
          </p>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Start Your UnityLove Journey</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Be among the first members helping shape a Black dating app built for real connection. Download the app or sign up on web—it's free to start.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Create Free Account
            </Link>
            <a
              href={BRAND.appStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors"
            >
              Get the App
            </a>
          </div>
        </section>

        {/* Choose Your Structure Section */}
        <section className="mb-12 bg-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Choose the Relationship Structure That Fits Your Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-600 text-sm">Multiple loving relationships practiced with honesty, consent, and open communication.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-600 text-sm">Structured multi-partner or multi-spouse commitment, often rooted in family, culture, and long-term responsibility.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-600 text-sm">Traditional one-partner commitment built on clarity, trust, and shared purpose.</p>
            </div>
          </div>
          <p className="text-center text-slate-700 mt-6">
            Whatever your structure, UnityLove prioritizes honesty, consent, and respect.
          </p>
        </section>

        {/* Related Pages */}
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Explore More</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black Poly Dating</Link>
            <Link to="/poly-dating-app" className="text-rose-600 hover:text-rose-700 underline">Poly Dating App</Link>
            <Link to="/ethical-non-monogamy-dating" className="text-rose-600 hover:text-rose-700 underline">Ethical Non-Monogamy</Link>
            <Link to="/about-unitylove" className="text-rose-600 hover:text-rose-700 underline">About UnityLove</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-600">
          <p>© {new Date().getFullYear()} UnityLove. The Afrocentric dating app for conscious Black singles.</p>
        </div>
      </footer>
    </div>
  );
}
